import useSWR, { type SWRConfiguration } from "swr";
import { productVariantsService } from "../../api_client";
import type { CompareVariantsDto } from "../../../api";
import type { VariantComparisonResponse } from "../data/variant-comparison.types";

/**
 * Fetches an AI-generated comparison of 2-3 product variants via the typed
 * `ProductVariantsApi.compareProductVariants` endpoint.
 */
export function useVariantComparison(
  variantIds: number[],
  focus?: string,
  options?: SWRConfiguration<VariantComparisonResponse>,
) {
  const uniqueIds = Array.from(new Set(variantIds)).filter(
    (id) => Number.isInteger(id) && id > 0,
  );
  const sortedIds = [...uniqueIds].sort((a, b) => a - b);
  const trimmedFocus = focus?.trim() ?? "";

  const shouldFetch = sortedIds.length >= 2 && sortedIds.length <= 3;
  const cacheKey = shouldFetch
    ? `variant-comparison:${sortedIds.join(",")}:${trimmedFocus}`
    : null;

  const swr = useSWR<VariantComparisonResponse>(
    cacheKey,
    async () => {
      const body: CompareVariantsDto = {
        variantIds: uniqueIds,
        ...(trimmedFocus ? { focus: trimmedFocus } : {}),
      } as CompareVariantsDto;
      const response = await productVariantsService.compareProductVariants(body);
      return response.data as unknown as VariantComparisonResponse;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
      ...options,
    },
  );

  return {
    ...swr,
    isLoading: shouldFetch && (swr.isLoading ?? (!swr.data && swr.isValidating)),
  };
}
